import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useMediaQuery,
  Chip,
  Box,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const PreferencesModal = ({
  open,
  colleges,
  initialMode,
  initialSelectedCollegeIds = [],
  onSave,
  ownCollegeName,
  showCloseButton = false,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [mode, setMode] = useState(initialMode);
  const [selectedCollegeIds, setSelectedCollegeIds] = useState(
    initialSelectedCollegeIds || []
  );

  useEffect(() => {
    setMode(initialMode);
    setSelectedCollegeIds(initialSelectedCollegeIds || []);
  }, [initialMode, initialSelectedCollegeIds]);

  const handleSaveClick = () => {
    if (mode === "SELECTED_COLLEGES" && selectedCollegeIds.length === 0) {
      alert("Please select at least one school to match with.");
      return;
    }

    onSave({
      mode,
      collegeIds: mode === "SELECTED_COLLEGES" ? selectedCollegeIds : [],
    });
  };

  const renderSelectedChips = (selected) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {selected.map((option) => (
        <Chip
          key={option.id}
          label={option.name}
          size="small"
          sx={{
            borderRadius: "999px",
            fontSize: "0.75rem",
            backgroundColor: "#dbeafe",
            border: "1px solid #60a5fa",
          }}
        />
      ))}
    </Box>
  );

  const modeCards = [
    {
      value: "ANY",
      title: "Anyone on Vibeu",
      subtitle: "Match with students from any school.",
      emoji: "🌎",
    },
    {
      value: "OWN_COLLEGE",
      title: "Only my school",
      subtitle: ownCollegeName
        ? `Keep it in ${ownCollegeName}`
        : "Keep it on your campus",
      emoji: "🏫",
    },
    {
      value: "SELECTED_COLLEGES",
      title: "Specific schools",
      subtitle: "Choose the schools you vibe with.",
      emoji: "🎯",
    },
  ];

  return (
    <Dialog
      open={open}
      // Only allow ESC/backdrop closing if we're explicitly making it dismissible
      onClose={showCloseButton && onClose ? onClose : undefined}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          background:
            "linear-gradient(135deg, #eff6ff 0%, #e0f2fe 40%, #ffffff 100%)",
          color: "#0f172a",
          boxShadow: "0 20px 60px rgba(15,23,42,0.35)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          pb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: 1.2,
                opacity: 0.9,
                fontSize: 11,
                color: "#64748b",
              }}
            >
              STEP 2 OF 2
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}
            >
              Who do you want to match with?
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.75, color: "#64748b" }}
            >
              Pick your matching preferences. You can change this anytime at Profile → Account Settings.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Chip
              label="Vibeu"
              size="small"
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: 11,
                backgroundColor: "#eef2ff",
                border: "1px solid #c7d2fe",
                color: "#1e293b",
              }}
            />

            {showCloseButton && onClose && (
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  borderRadius: "50%",
                  color: "#475569",
                  backgroundColor: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: "#e2e8f0",
                    color: "#0f172a",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3 },
          pt: 1.5,
          pb: { xs: 2, sm: 3 },
        }}
      >
        {/* Mode selection cards */}
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
          }}
        >
          {modeCards.map((m) => {
            const isActive = mode === m.value;
            return (
              <Box
                key={m.value}
                component="button"
                type="button"
                onClick={() => setMode(m.value)}
                sx={{
                  textAlign: "left",
                  borderRadius: 2,
                  border: `1px solid ${
                    isActive ? "#2563eb" : "rgba(148,163,184,0.6)"
                  }`,
                  backgroundColor: isActive ? "#dbeafe" : "#ffffff",
                  cursor: "pointer",
                  px: 1.6,
                  py: 1.2,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.1,
                  transition: "all 0.15s ease-out",
                  boxShadow: isActive
                    ? "0 10px 24px rgba(37,99,235,0.25)"
                    : "0 4px 12px rgba(148,163,184,0.25)",
                  "&:hover": {
                    borderColor: "#2563eb",
                    backgroundColor: "#eff6ff",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "999px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    backgroundColor: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    flexShrink: 0,
                  }}
                >
                  {m.emoji}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 0.15, color: "#0f172a" }}
                  >
                    {m.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b" }}
                  >
                    {m.value === "OWN_COLLEGE" && ownCollegeName ? (
                      <>
                        Keep it in{" "}
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#0f172a" }}
                        >
                          {ownCollegeName}
                        </Box>
                      </>
                    ) : (
                      m.subtitle
                    )}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "2px solid #94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {isActive && (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#2563eb",
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Searchable multi-select for schools */}
        {mode === "SELECTED_COLLEGES" && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 0.5, fontWeight: 600, fontSize: 13, color: "#0f172a" }}
            >
              Pick the schools you want to match with
            </Typography>
            <Autocomplete
              multiple
              options={colleges}
              getOptionLabel={(option) => option.name}
              value={colleges.filter((c) => selectedCollegeIds.includes(c.id))}
              onChange={(event, newValue) => {
                setSelectedCollegeIds(newValue.map((c) => c.id));
              }}
              filterSelectedOptions
              renderTags={(selected) => renderSelectedChips(selected)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search schools"
                  placeholder="Start typing a school name"
                  variant="outlined"
                  sx={{
                    backgroundColor: "#f8fafc",
                    borderRadius: 2.5,
                    "& .MuiOutlinedInput-root": {
                      color: "#0f172a", // dark input text
                    },
                    "& .MuiOutlinedInput-input::placeholder": {
                      color: "#64748b",
                      opacity: 1,
                    },
                    "& .MuiInputLabel-root": {
                      color: "#475569",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5e1",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2563eb",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2563eb",
                    },
                  }}
                />
              )}
              // Dropdown styling
              slotProps={{
                popper: {
                  sx: {
                    "& .MuiAutocomplete-paper": {
                      borderRadius: 2.5,
                      marginTop: 1,
                      boxShadow: "0 18px 40px rgba(15,23,42,0.25)",
                      border: "1px solid #e2e8f0",
                    },
                  },
                },
                paper: {
                  sx: {
                    backgroundColor: "#ffffff",
                    color: "#0f172a",
                    "& .MuiAutocomplete-listbox": {
                      padding: 0.5,
                    },
                  },
                },
              }}
              renderOption={(props, option, { selected }) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    borderRadius: 2,
                    padding: "8px 10px",
                    marginBottom: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "#0f172a",
                    "&.Mui-focused": {
                      backgroundColor: "#eff6ff",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#dbeafe !important",
                    },
                  }}
                >
                  <Typography variant="body2">{option.name}</Typography>
                  {selected && (
                    <CheckIcon
                      fontSize="small"
                      sx={{ color: "#2563eb", marginLeft: 1 }}
                    />
                  )}
                </Box>
              )}
              sx={{
                mt: 0.5,
                "& .MuiAutocomplete-clearIndicator": { color: "#0f172a" },
                "& .MuiAutocomplete-popupIndicator": { color: "#0f172a" },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          pb: { xs: 2.5, sm: 3 },
          pt: 1,
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSaveClick}
          sx={{
            px: 3,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#2563eb",
            boxShadow: "0 10px 25px rgba(37,99,235,0.35)",
            "&:hover": {
              backgroundColor: "#1d4ed8",
              boxShadow: "0 14px 32px rgba(37,99,235,0.5)",
            },
          }}
        >
          Save & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PreferencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  colleges: PropTypes.array.isRequired, // [{ id, name }]
  initialMode: PropTypes.string,
  initialSelectedCollegeIds: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  ownCollegeName: PropTypes.string,
  showCloseButton: PropTypes.bool,
  onClose: PropTypes.func,
};

export default PreferencesModal;
